/**
 * Dificuldade provisoria: ENVELOPE, nunca ponto.
 *
 * Nao ha pre-testagem, logo nao existe parametro calibrado. A postura honesta e dupla:
 * publicar um intervalo com proveniencia declarada, e instrumentar tudo para calibrar
 * assim que houver amostra.
 *
 * O modelo aditivo herda a secao 2.3 do catalogo de sintese, que ja descreve
 * Facil/Media/Dificil em termos de numero de etapas, inversao e integracao de suportes.
 *
 *     b^ = b0(task-model) + soma dos modificadores da variante
 *
 * ALERTA QUE PRECISA SER DITO EM VOZ ALTA: variantes geradas automaticamente NAO sao
 * psicometricamente equivalentes. Trocar "40 alunos, 26 aprovados" por "35, 21" muda b,
 * porque um da conta redonda e o outro nao. Tratar todas as variantes de um template
 * como o mesmo item enviesa a estimativa de proficiencia. Por isso `variantSources`
 * existe -- ele e, ao mesmo tempo, o modificador de b e o vetor de features para um
 * modelo TRI explicativo (LLTM) quando os dados chegarem.
 */

import type { CognitiveAxis } from './taxonomy.ts';

export type DifficultySourceCode =
  // --- estrutura do raciocinio -------------------------------------------------
  | 'steps.1'
  | 'steps.2'
  | 'steps.3plus'
  | 'inverse-problem'
  | 'combined-skill'
  // --- suportes e leitura ------------------------------------------------------
  | 'two-supports'
  | 'irrelevant-data'
  | 'select-information'
  | 'reading-load.high'
  | 'dense-context'
  // --- carga numerica ----------------------------------------------------------
  | 'non-round-numbers'
  | 'unit-conversion'
  | 'precision-demand'
  // --- alternativas ------------------------------------------------------------
  | 'distractor-proximity.high'
  // --- especificos de N3/N4 ----------------------------------------------------
  /** Quanto mais ferramentas no cinto, maior o espaco de busca. */
  | 'construction.free-tools'
  /** Construcao longa: mais passos minimos ate o objetivo. */
  | 'construction.long'
  /** Sem feedback durante a construcao. */
  | 'construction.no-hint'
  /** O aluno precisa escolher onde comecar (nao ha ponto de partida obvio). */
  | 'construction.open-start';

export interface DifficultySource {
  readonly code: DifficultySourceCode;
  readonly label: string;
  /** Contribuicao aditiva, em logits. */
  readonly delta: number;
  /** Incerteza desta contribuicao, em logits; alimenta a largura do envelope. */
  readonly sigma: number;
}

function src(
  code: DifficultySourceCode,
  label: string,
  delta: number,
  sigma: number,
): DifficultySource {
  return { code, label, delta, sigma };
}

/**
 * Pesos a priori. Todos sao PALPITES DE ESPECIALISTA, nao estimativas -- e o campo
 * `status: 'a-priori'` no envelope existe para que essa distincao nunca se perca.
 * Quando houver dados, um modelo LLTM substitui cada `delta` pelo beta empirico.
 */
export const DIFFICULTY_SOURCES: Readonly<Record<DifficultySourceCode, DifficultySource>> =
  Object.freeze({
    'steps.1': src('steps.1', 'Uma etapa', -0.4, 0.2),
    'steps.2': src('steps.2', 'Duas etapas', 0.3, 0.25),
    'steps.3plus': src('steps.3plus', 'Três ou mais etapas', 0.8, 0.35),
    'inverse-problem': src('inverse-problem', 'Problema inverso', 0.6, 0.3),
    'combined-skill': src('combined-skill', 'Habilidade combinada com outra', 0.5, 0.3),

    'two-supports': src('two-supports', 'Integração de dois suportes', 0.5, 0.25),
    'irrelevant-data': src('irrelevant-data', 'Dado irrelevante a descartar', 0.3, 0.2),
    'select-information': src('select-information', 'Seleção da informação necessária', 0.4, 0.25),
    'reading-load.high': src('reading-load.high', 'Carga de leitura alta', 0.25, 0.2),
    'dense-context': src('dense-context', 'Contexto denso', 0.2, 0.2),

    'non-round-numbers': src('non-round-numbers', 'Números não redondos', 0.3, 0.2),
    'unit-conversion': src('unit-conversion', 'Conversão de unidade', 0.35, 0.2),
    'precision-demand': src('precision-demand', 'Precisão numérica exigida', 0.25, 0.2),

    'distractor-proximity.high': src(
      'distractor-proximity.high',
      'Distratores muito próximos da chave',
      0.35,
      0.25,
    ),

    'construction.free-tools': src(
      'construction.free-tools',
      'Cinto sem a ferramenta que resolve direto',
      0.7,
      0.4,
    ),
    'construction.long': src('construction.long', 'Construção com muitos passos', 0.5, 0.3),
    'construction.no-hint': src('construction.no-hint', 'Sem dica durante a construção', 0.3, 0.25),
    'construction.open-start': src(
      'construction.open-start',
      'Sem ponto de partida óbvio',
      0.4,
      0.3,
    ),
  });

/**
 * De onde veio o numero. A distincao NUNCA se perde -- e o que separa uma estimativa
 * de um palpite bem-intencionado.
 */
export type CalibrationStatus =
  | 'a-priori' // modelo aditivo, sem nenhum dado
  | 'expert-reviewed' // revisado por especialista (tipo Angoff)
  | 'piloted' // ha dados, mas amostra insuficiente
  | 'calibrated'; // parametros estimados de fato

/**
 * Modelo de resposta. NAO assumir 3PL.
 *
 * O parametro c (acerto ao acaso) existe porque ha 5 alternativas. Numa construcao
 * geometrica livre, o acaso nao produz uma bissetriz -- logo 3PL nao se aplica a N4.
 */
export type ResponseModel =
  | '3PL' // multipla escolha
  | '2PL' // construcao/marcacao dicotomica
  | 'GPCM'; // credito parcial

export interface DifficultyEnvelope {
  /** Piso do template, em logits, na escala theta da rede. */
  readonly base: number;
  /** Fontes declaradas pelo TEMPLATE (constantes entre variantes). */
  readonly templateSources: readonly DifficultySource[];
  /** Fontes contribuidas por ESTA variante (vindas do gerador). */
  readonly variantSources: readonly DifficultySource[];
  /** base + soma dos deltas. */
  readonly provisionalB: number;
  /** provisionalB -/+ z * sqrt(soma dos sigma^2), com z = 1,645 (90%). */
  readonly lo: number;
  readonly hi: number;
  readonly status: CalibrationStatus;
  readonly responseModel: ResponseModel;
  /** Estimativas empiricas, quando existirem. `null` ate la -- nunca um numero inventado. */
  readonly calibratedB: number | null;
  readonly calibratedA: number | null;
  readonly calibratedC: number | null;
  readonly nResponses: number;
}

const Z90 = 1.645;

export function envelope(
  base: number,
  templateCodes: readonly DifficultySourceCode[],
  variantCodes: readonly DifficultySourceCode[],
  responseModel: ResponseModel,
  status: CalibrationStatus = 'a-priori',
): DifficultyEnvelope {
  const templateSources = templateCodes.map((c) => DIFFICULTY_SOURCES[c]);
  const variantSources = variantCodes.map((c) => DIFFICULTY_SOURCES[c]);
  const all = [...templateSources, ...variantSources];

  const provisionalB = base + all.reduce((s, x) => s + x.delta, 0);
  const sd = Math.sqrt(all.reduce((s, x) => s + x.sigma * x.sigma, 0));
  const halfWidth = Z90 * Math.max(sd, 0.15); // piso: nunca alegar certeza absoluta

  return {
    base,
    templateSources,
    variantSources,
    provisionalB,
    lo: provisionalB - halfWidth,
    hi: provisionalB + halfWidth,
    status,
    responseModel,
    calibratedB: null,
    calibratedA: null,
    calibratedC: null,
    nResponses: 0,
  };
}

/**
 * Vetor 0/1 sobre as fontes de dificuldade, para a telemetria.
 *
 * E o que permite ajustar depois um modelo TRI explicativo (LLTM):
 *     b_variante = b_template + soma(beta_k * feature_k)
 * Voce aprende o custo empirico em logits de "mais uma etapa" no banco INTEIRO, e
 * esses betas passam a dar prior calibrado para templates novos no primeiro dia.
 *
 * IMPOSSIVEL DE RECONSTRUIR RETROATIVAMENTE: se este vetor nao for gravado junto com
 * a resposta, o modelo explicativo fica permanentemente indisponivel para todos os
 * dados coletados antes da correcao.
 */
export function variantFeatureVector(
  env: DifficultyEnvelope,
): Readonly<Partial<Record<DifficultySourceCode, 1>>> {
  const out: Partial<Record<DifficultySourceCode, 1>> = {};
  for (const s of [...env.templateSources, ...env.variantSources]) out[s.code] = 1;
  return out;
}

// ---------------------------------------------------------------------------
// Piso de dificuldade: derivado, nunca inventado
// ---------------------------------------------------------------------------

/**
 * O PISO `b0` SAI DE PROPRIEDADES OBJETIVAS DA MATRIZ, não do gosto de quem escreve.
 *
 * Antes, cada template trazia um `difficultyBase` escolhido a olho (−0,3; 0,0; 0,4…).
 * Números assim não dizem nada: não são comparáveis entre itens, não têm âncora e não
 * há como auditá-los. Um valor provisório só presta se for CONSISTENTE — se dois itens
 * com o mesmo perfil receberem o mesmo piso, sempre, por construção.
 *
 * A âncora: `b = 0` é o aluno mediano do 2º ano do Ensino Médio, num item de Eixo II.
 * A partir dela, duas contribuições, ambas lidas da própria matriz CAEd:
 *
 *   EIXO COGNITIVO — o que o item pede fazer
 *     I   reconhecer, converter registros      −0,35
 *     II  resolver uma situação-problema        0,00   (referência)
 *     III avaliar, julgar, ajustar             +0,45
 *
 *   ANO — onde a habilidade é ensinada
 *     1º  −0,20     2º  0,00  (referência)     3º  +0,20
 *
 * O resto da dificuldade vem dos modificadores declarados (etapas, inversão,
 * integração de suportes…), que já são aditivos e já entram no envelope. Assim o
 * número inteiro é rastreável: piso do perfil + soma de fontes nomeadas.
 *
 * Isto continua sendo julgamento, não medida — e o campo `status: 'a-priori'` diz isso.
 * O que mudou é que agora é julgamento com regra, e a regra vale para o banco todo.
 */
const PESO_EIXO: Readonly<Record<CognitiveAxis, number>> = {
  I: -0.35,
  II: 0.0,
  III: 0.45,
};

const PESO_ANO: Readonly<Record<1 | 2 | 3, number>> = {
  1: -0.2,
  2: 0.0,
  3: 0.2,
};

/** O ano vem do prefixo do código da habilidade: H1.x é 1º ano, H2.x é 2º, H3.x é 3º. */
export function anoDaHabilidade(codigo: string): 1 | 2 | 3 {
  const m = /^H([123])\./.exec(codigo);
  return m ? (Number(m[1]) as 1 | 2 | 3) : 2;
}

export function pisoDeDificuldade(eixo: CognitiveAxis, skills: readonly string[]): number {
  const anos = skills.map((s) => anoDaHabilidade(s));
  // Item que mobiliza habilidades de anos diferentes assume o mais avançado.
  const ano = anos.length > 0 ? (Math.max(...anos) as 1 | 2 | 3) : 2;
  return PESO_EIXO[eixo] + PESO_ANO[ano];
}
